import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { useAccountStore } from '@components/Stores/accountStore';
import { getAllRoutes } from '@sp-firebase/routesFB';
import { IMaterialToAccount, IRiskTolerance } from '@sp-types/accountTypes';
import { IRoute, IRoutePolicy } from '@sp-types/index';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAllProducts } from '../../../hooks/useProducts';
import { createRoutePolicy, getRoutePolicies } from '../../../sp-firebase/routePolicyFB';
import { Product } from '../../../sp-types/productType';
import { makeMap, makeRoutePolicy } from '@utils/routesUtils';
import { MaterialTrendButton } from '../../Dashboard/MaterialTrendButton';
import { getMaterialsCardData } from '../../Dashboard/dashboardUtils';
import { EditExposure } from './components/EditExposure';
import { EditRouteSettings } from './components/EditRoutesSettings';
import { ExposureMonitor } from './components/ExposureMonitor';
import { getItemsOfMaterial } from './components/MaterialDetailUtils';
import { MaterialPricing } from './components/MaterialPricing';
import { MaterialProducts } from './components/MaterialProducts';
import { MaterialRoutes } from './components/MaterialRoutes';
import { SkeletonMaterial } from './components/SkeletonMaterial';
import { useExposureStore, GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { useExposure } from '@hooks/useExposure';
import { updateAccountProperty } from '../../../sp-firebase/accountFB';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
const ADD_PRODUCTS_URL = '/setting-product-create/create-new';

/* eslint-disable */
enum ViewMode {
  View,
  EditRoutes,
  EditExposure,
}
/* eslint-enable */

export const MaterialDetails: React.FC = () => {
  const { account, materialColorDictionary: colorsMap, setMaterialToAccount } = useAccountStore();
  const { data: products } = useAllProducts(account?.id);
  const { materialsMap } = useMaterialsStore();
  const { convertionMap } = useCurrencyRates();
  const [pageMode, setPageMode] = React.useState<ViewMode>(ViewMode.View as ViewMode);
  const [selectedRoute, setSelectedRoute] = React.useState<IRoute | undefined>();
  const [routePolicies, setRoutePolicies] = React.useState<IRoutePolicy[] | undefined>();
  const [isLoading, setIsLoading] = React.useState(false);

  const navigate = useNavigate();

  const { refetch: fetchExposure } = useExposure(account?.id);
  const {
    netExposure,
    currentSelectedMaterialId: materialId,
    setCurrentSelectedMaterialId: setMaterialId,
  } = useExposureStore();

  const [allRoutes, setAllRoutes] = React.useState<IRoute[] | undefined>();

  async function getRoutes() {
    setIsLoading(true);

    const _allRoutes = await getAllRoutes();

    const materialRoutes = getItemsOfMaterial(_allRoutes || [], materialId);

    setAllRoutes(materialRoutes);
    setSelectedRoute(materialRoutes?.[0]);
    setIsLoading(false);
  }

  const fetchRoutePolicies = async (accountId: string) => {
    const policies = await getRoutePolicies(accountId);

    setRoutePolicies(policies);
  };

  const onOrderChange = async (reorderedList: IRoute[]) => {
    const existingPolicyMap = makeMap(routePolicies || [], 'routeId');

    const updatedRoutePolicies = reorderedList.map((route) =>
      makeRoutePolicy(route, account?.id || '', materialId, existingPolicyMap[route.id]),
    );

    await Promise.all(updatedRoutePolicies.map((policy) => createRoutePolicy(policy)));
    saveAccountMaterials(
      updatedRoutePolicies.map(({ routeId, order }) => ({ routeId, order })),
      materialId,
    );

    await updateAccountProperty(account?.id || '', 'materialsToAccount', account.materialsToAccount);

    fetchRoutePolicies(account?.id || '');
  };

  React.useEffect(() => {
    if (!materialId || materialId === GENERAL_ID) {
      const firstMaterial = account.materialsToAccount?.[1]?.materialId;
      firstMaterial && setMaterialId(firstMaterial);
    }
  }, [materialsMap, account]);

  // temporary
  React.useEffect(() => {
    if (materialId !== GENERAL_ID) {
      getRoutes();
    }
  }, [materialId]);

  React.useEffect(() => {
    if (account?.id) {
      fetchRoutePolicies(account.id);

      fetchExposure();
    }
  }, [account]);

  const getAccountMaterial = (materialId: string): IMaterialToAccount | undefined =>
    account.materialsToAccount.find((a) => a.materialId === materialId);

  const getProducts = (materialId: string): Product[] | undefined =>
    products?.filter((p) => p.ingredients?.filter(({ materialID }) => materialID === materialId).length > 0);

  const onSaveRouteCond = async (policy: IRoutePolicy) => {
    if (account?.id) {
      setIsLoading(true);
      await createRoutePolicy({ ...policy, accountId: account.id });

      await fetchRoutePolicies(account.id);
      setIsLoading(false);
    }
  };

  const saveRiskBoundries = async (RiskTolerance: IRiskTolerance, currMaterialId: string) => {
    const updatedMaterials = account.materialsToAccount.map((mat) =>
      mat.materialId === currMaterialId ? { ...mat, RiskTolerance } : mat,
    );

    setMaterialToAccount(updatedMaterials);

    await updateAccountProperty(account?.id || '', 'materialsToAccount', updatedMaterials);
  };

  const saveAccountMaterials = (RoutePriority: { routeId: string; order: number }[], currMaterialId: string) => {
    setMaterialToAccount(
      account.materialsToAccount.map((mat) => (mat.materialId === currMaterialId ? { ...mat, RoutePriority } : mat)),
    );
  };

  return materialId ? (
    <div className="flex flex-col flex-1 px-2 h-full  gap-3 pb-2 overflow-auto ">
      <MaterialTrendButton
        buttons={getMaterialsCardData(
          materialsMap,
          colorsMap,
          convertionMap,
          account.materialsToAccount, //.filter((a) => a.materialId !== GENERAL_ID),
          netExposure,
        )}
        currentMaterial={materialId}
        setCurrentMaterial={setMaterialId}
      />
      <div className="flex flex-1 flex-row gap-3  overflow-hidden   ">
        <div className="flex flex-col flex-1 gap-3 ">
          <div className="bg-white shadow-custom flex-1 overflow-hidden min-w-full ">
            {materialsMap[materialId] ? (
              <MaterialPricing
                material={materialsMap[materialId]}
                isView={pageMode === ViewMode.View}
                onEdit={() => setPageMode((mode) => (mode === ViewMode.View ? ViewMode.EditRoutes : ViewMode.View))}
                routes={allRoutes}
                selectedRoute={selectedRoute}
              />
            ) : (
              <div className="p-4 mx-auto">Select material</div>
            )}
          </div>
          <div className="flex flex-1  shadow-custom bg-white ">
            {pageMode === ViewMode.EditExposure ? (
              <EditExposure
                {...{ materialId, netExposure, convertionMap }}
                accountMaterial={getAccountMaterial(materialId)}
                onCancel={() => setPageMode(ViewMode.View)}
                onSave={(risk) => saveRiskBoundries(risk, materialId)}
              />
            ) : (
              <>
                <div className="flex-1 h-[45vh] overflow-y-auto  p-3">
                  <MaterialRoutes
                    routes={allRoutes}
                    {...{ selectedRoute, setSelectedRoute, materialId, isLoading, onOrderChange, materialsMap }}
                    isEdit={pageMode === ViewMode.EditRoutes}
                    routesPolicies={routePolicies}
                    dragEnabled
                  />
                </div>
                {pageMode === ViewMode.EditRoutes && (
                  <div className="flex flex-col h-full bg-white w-[25rem]  ">
                    <EditRouteSettings
                      route={selectedRoute}
                      routePolicies={routePolicies}
                      cancelEdit={() => setPageMode(ViewMode.View)}
                      onSave={(policy) => onSaveRouteCond(policy)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {pageMode === ViewMode.View && (
          <div className="flex flex-col  gap-3 overflow-hidden  w-80 ">
            <div className="flex-1 bg-white shadow-custom overflow-auto">
              <ExposureMonitor
                materialId={materialId}
                accountMaterial={getAccountMaterial(materialId)}
                netExposure={netExposure}
                convertionMap={convertionMap}
                onEdit={() => setPageMode(ViewMode.EditExposure)}
              />
            </div>
            <div className="flex-1 bg-white shadow-custom overflow-hidden">
              <MaterialProducts
                onClickAddProduct={() => navigate(ADD_PRODUCTS_URL)}
                material={materialsMap[materialId]}
                products={getProducts(materialId)}
                colors={colorsMap}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  ) : (
    <SkeletonMaterial />
  );
};
